import { useState } from 'react';
import {
    Box,
    CircularProgress,
    InputLabel,
    MenuItem,
    Select,
    SelectChangeEvent,
    Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import { IWorkspacePlanModel } from 'api/models/response/workspacePlan/IWorkspacePlanModel';
import { IWorkspaceSubscriptionModel } from 'api/models/response/workspacePlan/IWorkspaceSubscriptionModel';
import { useUpdateWorkspaceSubscriptionPlan } from 'hooks/workspacePlan/useUpdateWorkspaceSubscriptionPlan';
import { handleApiError } from 'helpers/handleApiError';
import { ConfirmationDialog } from 'components/ConfirmationDialog';
import { WorkspaceStorageUsage } from 'components/WorkspaceStorageUsage';
import { UNLIMITED } from './workspacePlanForm';
import {
    PlanNameText,
    PlanSelectFormControl,
    SectionHeader,
    StorageContainer,
    SubscriptionDetails,
    SubscriptionPaper,
} from './SubscriptionPlan.styles';

interface ICurrentSubscriptionSectionProps {
    subscription: IWorkspaceSubscriptionModel;
    plans: IWorkspacePlanModel[];
    refetching: boolean;
}

export const CurrentSubscriptionSection = ({ subscription, plans, refetching }: ICurrentSubscriptionSectionProps) => {
    const { t } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();
    const { changeSubscriptionPlan, changingSubscriptionPlan } = useUpdateWorkspaceSubscriptionPlan();
    const [planToAssign, setPlanToAssign] = useState<IWorkspacePlanModel | null>(null);

    const currentPlan = plans.find((plan) => plan.id === subscription.planId);

    const formatLimit = (value?: number) => {
        if (value === undefined) return '-';
        return value === UNLIMITED ? t('subscriptionPlan.unlimited') : String(value);
    };

    const handlePlanSelect = (e: SelectChangeEvent<string>) => {
        const selected = plans.find((plan) => plan.code === e.target.value);
        if (selected && selected.code !== subscription.planCode) {
            setPlanToAssign(selected);
        }
    };

    const handleConfirmChange = async () => {
        if (!planToAssign) {
            return;
        }

        try {
            await changeSubscriptionPlan(planToAssign.code);
            enqueueSnackbar(t('subscriptionPlan.changePlanSuccess', { name: planToAssign.name }), { variant: 'success' });
        } catch (error) {
            handleApiError(error, { t, enqueueSnackbar });
        } finally {
            setPlanToAssign(null);
        }
    };

    return (
        <SubscriptionPaper variant="outlined" data-testid="current-subscription">
            <SectionHeader>
                <Typography variant="h6">{t('subscriptionPlan.currentSubscription')}</Typography>
                {(refetching || changingSubscriptionPlan) && <CircularProgress size={16} />}
            </SectionHeader>

            <SubscriptionDetails>
                <Box>
                    <Typography variant="body2" color="textSecondary">{t('subscriptionPlan.plan')}</Typography>
                    <PlanNameText variant="subtitle1" data-testid="current-plan-name">
                        {subscription.planName}
                    </PlanNameText>
                    <Typography variant="caption" color="textSecondary">{subscription.planCode}</Typography>
                </Box>

                <Box>
                    <Typography variant="body2" color="textSecondary">{t('subscriptionPlan.maxUsers')}</Typography>
                    <Typography variant="subtitle1">{formatLimit(currentPlan?.maxUsers)}</Typography>
                </Box>

                <Box>
                    <Typography variant="body2" color="textSecondary">{t('subscriptionPlan.maxGogglesCount')}</Typography>
                    <Typography variant="subtitle1">{formatLimit(currentPlan?.maxGogglesCount)}</Typography>
                </Box>

                <StorageContainer>
                    <Typography variant="body2" color="textSecondary">{t('subscriptionPlan.storage')}</Typography>
                    <WorkspaceStorageUsage fullWidth />
                </StorageContainer>

                <PlanSelectFormControl size="small">
                    <InputLabel id="subscription-plan-select-label">{t('subscriptionPlan.changePlan')}</InputLabel>
                    <Select
                        labelId="subscription-plan-select-label"
                        label={t('subscriptionPlan.changePlan')}
                        value={plans.some((plan) => plan.code === subscription.planCode) ? subscription.planCode : ''}
                        onChange={handlePlanSelect}
                        disabled={changingSubscriptionPlan || plans.length === 0}
                        data-testid="subscription-plan-select"
                    >
                        {plans.map((plan) => (
                            <MenuItem key={plan.id} value={plan.code}>
                                {plan.name}
                            </MenuItem>
                        ))}
                    </Select>
                </PlanSelectFormControl>
            </SubscriptionDetails>

            <ConfirmationDialog
                open={!!planToAssign}
                message={t('subscriptionPlan.changePlanConfirm', { name: planToAssign?.name ?? '' })}
                handleClose={() => setPlanToAssign(null)}
                handleConfirm={handleConfirmChange}
            />
        </SubscriptionPaper>
    );
};
